import {
  FileText,
  Library,
  MessagesSquare,
  Users,
  type LucideIcon,
} from "lucide-react";
import { APPS_HOME_PATH } from "@/lib/apps";
import { BACK_TO_GLOBAL_CHAT_LABEL, WORKSPACE_GLOBAL_CHAT_LABEL } from "./constants.js";

export interface RagNavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
}

export interface RagNavSubItem {
  id: string;
  to: string;
  label: string;
}

export type RagNavCreateChat =
  | { scope: "library"; workspaceId: string; libraryId: string }
  | { scope: "workspace"; workspaceId: string }
  | { scope: "publicLibrary"; libraryId: string }
  | { scope: "regulatoryGlobal" };

export interface RagNavWorkspaceItem {
  id: string;
  to: string;
  label: string;
  kind: "PERSONAL" | "ORGANIZATION";
  description?: string;
  libraryCount: number;
  memberCount: number;
}

export interface RagNavWorkspaceSection {
  label: string;
  description?: string;
  items: RagNavWorkspaceItem[];
}

export interface RagNavConfig {
  sectionLabel: string;
  items: RagNavItem[];
  workspaceSections?: RagNavWorkspaceSection[];
  activeWorkspaceId?: string;
  showNewWorkspace?: boolean;
  subSectionLabel?: string;
  subItems?: RagNavSubItem[];
  /** When true, Sidebar shows a client-side search filter above Recent subItems. */
  subItemsSearchable?: boolean;
  footerAction?: {
    to: string;
    label: string;
    createChat?: RagNavCreateChat;
  };
}

/** Default KM landing for a workspace (new / global chat). */
export function workspaceHomePath(workspaceId: string): string {
  return `/rag/workspaces/${workspaceId}/chat`;
}

function workspaceNavItems(workspaceId: string): RagNavItem[] {
  return [
    {
      to: `/rag/workspaces/${workspaceId}/chat`,
      label: WORKSPACE_GLOBAL_CHAT_LABEL,
      icon: MessagesSquare,
    },
    {
      to: `/rag/workspaces/${workspaceId}`,
      label: "Libraries",
      icon: Library,
      end: true,
    },
    {
      to: `/rag/workspaces/${workspaceId}/members`,
      label: "Members",
      icon: Users,
      end: true,
    },
  ];
}

export function buildWorkspaceNav(workspaceId: string, _workspaceName: string): RagNavConfig {
  return {
    sectionLabel: "KM",
    activeWorkspaceId: workspaceId,
    items: workspaceNavItems(workspaceId),
  };
}

export interface RagNavWorkspaceInput {
  id: string;
  name: string;
  description: string;
  kind: "PERSONAL" | "ORGANIZATION";
  ownerId: string;
  libraryCount: number;
  memberCount: number;
}

export function buildWorkspaceNavSections(
  workspaces: RagNavWorkspaceInput[],
  currentUserId: string | undefined,
): RagNavWorkspaceSection[] {
  const organizationWorkspaces = workspaces.filter((workspace) => workspace.kind === "ORGANIZATION");
  const ownedPersonalWorkspaces = workspaces.filter(
    (workspace) => workspace.kind === "PERSONAL" && workspace.ownerId === currentUserId,
  );
  const sharedPersonalWorkspaces = workspaces.filter(
    (workspace) => workspace.kind === "PERSONAL" && workspace.ownerId !== currentUserId,
  );

  function toNavItem(
    workspace: RagNavWorkspaceInput,
    sectionDescription?: string,
  ): RagNavWorkspaceItem {
    let description: string | undefined;
    if (workspace.kind !== "ORGANIZATION") {
      const trimmed = workspace.description?.trim();
      if (trimmed && trimmed !== sectionDescription?.trim()) {
        description = trimmed;
      }
    }

    return {
      id: workspace.id,
      to: workspaceHomePath(workspace.id),
      label: workspace.name,
      kind: workspace.kind,
      description,
      libraryCount: workspace.libraryCount,
      memberCount: workspace.memberCount,
    };
  }

  const sections: RagNavWorkspaceSection[] = [];

  if (organizationWorkspaces.length > 0) {
    const orgSectionDescription = "Shared workspace for your organization";
    sections.push({
      label: "Default workspace",
      description: orgSectionDescription,
      items: organizationWorkspaces.map((workspace) => toNavItem(workspace, orgSectionDescription)),
    });
  }

  if (sharedPersonalWorkspaces.length > 0) {
    const sharedSectionDescription = "Workspaces others have shared with you";
    sections.push({
      label: "Shared workspaces",
      description: sharedSectionDescription,
      items: sharedPersonalWorkspaces.map((workspace) =>
        toNavItem(workspace, sharedSectionDescription),
      ),
    });
  }

  if (ownedPersonalWorkspaces.length > 0) {
    sections.push({
      label: "Personal workspaces",
      items: ownedPersonalWorkspaces.map((workspace) => toNavItem(workspace)),
    });
  }

  return sections;
}

export const KM_LAST_WORKSPACE_KEY = "doculegis-km-last-workspace-id";

export function getLastKmWorkspaceId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const item = window.localStorage.getItem(KM_LAST_WORKSPACE_KEY);
    if (!item) return null;
    const parsed: unknown = JSON.parse(item);
    return typeof parsed === "string" && parsed.length > 0 ? parsed : null;
  } catch {
    return null;
  }
}

export function setLastKmWorkspaceId(workspaceId: string | null): void {
  if (typeof window === "undefined") return;
  try {
    if (workspaceId) {
      window.localStorage.setItem(KM_LAST_WORKSPACE_KEY, JSON.stringify(workspaceId));
    } else {
      window.localStorage.removeItem(KM_LAST_WORKSPACE_KEY);
    }
  } catch {
    // Ignore storage failures (private mode, quota, etc.)
  }
}

export function pickDefaultWorkspaceId(
  workspaces: RagNavWorkspaceInput[],
  currentUserId: string | undefined,
  preferredWorkspaceId?: string | null,
): string | null {
  if (!workspaces.length) return null;

  if (
    preferredWorkspaceId &&
    workspaces.some((workspace) => workspace.id === preferredWorkspaceId)
  ) {
    return preferredWorkspaceId;
  }

  const organization = workspaces.find((workspace) => workspace.kind === "ORGANIZATION");
  if (organization) return organization.id;

  const ownedPersonal = workspaces.find(
    (workspace) => workspace.kind === "PERSONAL" && workspace.ownerId === currentUserId,
  );
  if (ownedPersonal) return ownedPersonal.id;

  return workspaces[0]?.id ?? null;
}

function withWorkspaceSidebar(
  config: RagNavConfig,
  workspaceSections: RagNavWorkspaceSection[],
  showNewWorkspace: boolean,
): RagNavConfig {
  return {
    ...config,
    sectionLabel: "KM",
    workspaceSections,
    showNewWorkspace,
  };
}

export function buildKmRootNav(
  workspaceSections: RagNavWorkspaceSection[],
  showNewWorkspace: boolean,
): RagNavConfig {
  return {
    sectionLabel: "KM",
    items: [],
    workspaceSections,
    showNewWorkspace,
  };
}

export function mergeWorkspaceSidebar(
  config: RagNavConfig,
  workspaceSections: RagNavWorkspaceSection[],
  showNewWorkspace: boolean,
): RagNavConfig {
  return withWorkspaceSidebar(config, workspaceSections, showNewWorkspace);
}

export function buildWorkspaceChatNav(
  workspaceId: string,
  _workspaceName: string,
  recentChats: { id: string; title: string }[] = []
): RagNavConfig {
  return {
    sectionLabel: "KM",
    activeWorkspaceId: workspaceId,
    items: workspaceNavItems(workspaceId),
    subSectionLabel: recentChats.length ? "Recent" : undefined,
    subItems: recentChats.map((chat) => ({
      id: chat.id,
      to: `/rag/workspaces/${workspaceId}/chat/${chat.id}`,
      label: chat.title,
    })),
    footerAction: {
      to: `/rag/workspaces/${workspaceId}/chat`,
      label: "New chat",
      createChat: { scope: "workspace", workspaceId },
    },
  };
}

export function buildLibraryNav(
  workspaceId: string,
  libraryId: string,
  libraryName: string,
): RagNavConfig {
  return {
    sectionLabel: libraryName,
    items: [
      {
        to: `/rag/workspaces/${workspaceId}/libraries/${libraryId}/documents`,
        label: "Documents",
        icon: FileText,
      },
    ],
  };
}

export function getRagActiveSubItem(pathname: string): string | null {
  const libraryChat = pathname.match(/\/chats\/([^/]+)/);
  if (libraryChat) return libraryChat[1];

  const workspaceChat = pathname.match(/\/chat\/([^/]+)/);
  if (workspaceChat) return workspaceChat[1];

  return null;
}

export interface KmBackLink {
  to: string;
  label: string;
}

export function resolveKmBackLink(pathname: string): KmBackLink {
  if (pathname === "/rag" || pathname === "/rag/") {
    return { to: APPS_HOME_PATH, label: "Back to apps" };
  }

  const workspaceMatch = pathname.match(/^\/rag\/workspaces\/([^/]+)/);
  if (!workspaceMatch) {
    return { to: APPS_HOME_PATH, label: "Back to apps" };
  }

  const workspaceId = workspaceMatch[1];
  const workspaceBase = `/rag/workspaces/${workspaceId}`;

  const libraryChatMatch = pathname.match(
    /^\/rag\/workspaces\/[^/]+\/libraries\/([^/]+)\/chats\/([^/]+)/,
  );
  if (libraryChatMatch) {
    return {
      to: `${workspaceBase}/libraries/${libraryChatMatch[1]}/chats`,
      label: "Back to chats",
    };
  }

  const libraryChatsMatch = pathname.match(
    /^\/rag\/workspaces\/[^/]+\/libraries\/([^/]+)\/chats\/?$/,
  );
  if (libraryChatsMatch) {
    return { to: workspaceBase, label: "Back to workspace" };
  }

  const libraryDocumentsMatch = pathname.match(
    /^\/rag\/workspaces\/[^/]+\/libraries\/([^/]+)\/documents\/?$/,
  );
  if (libraryDocumentsMatch) {
    return { to: workspaceBase, label: "Back to workspace" };
  }

  const workspaceChatMatch = pathname.match(/^\/rag\/workspaces\/[^/]+\/chat\/([^/]+)/);
  if (workspaceChatMatch) {
    return { to: `${workspaceBase}/chat`, label: BACK_TO_GLOBAL_CHAT_LABEL };
  }

  if (
    pathname === `${workspaceBase}/chat` ||
    pathname === `${workspaceBase}/chat/` ||
    pathname === `${workspaceBase}/members` ||
    pathname === `${workspaceBase}/members/`
  ) {
    return { to: APPS_HOME_PATH, label: "Back to apps" };
  }

  if (pathname === workspaceBase || pathname === `${workspaceBase}/`) {
    return { to: APPS_HOME_PATH, label: "Back to apps" };
  }

  return { to: APPS_HOME_PATH, label: "Back to apps" };
}
